import * as semver from 'semver';
import {
  isValidExtensionCompositeId,
  isValidPublisherName,
} from '@kirin/api-client/contracts/extension-validators';

import type {
  EditableRow,
  ValidateCellFn,
} from '@/components/editable-row-table';

import { policyRuleToExceptionRuleObject } from './adapters';
import type { AllowBlockPolicyKey } from '../types';

/** Shown on the inline status chip when name + publisher are both empty. */
const MISSING_IDENTIFIER_BADGE = 'Missing';

/** Matches backend unique constraint on policy_exceptions.ruleObject (see POLICY_EXCEPTION_DUPLICATE). */
const DUPLICATE_BADGE = 'Duplicate';

const INVALID_EXTENSION_ID_MSG = 'Invalid extension id';
const INVALID_PUBLISHER_MSG = 'Invalid publisher name';

/**
 * Validates Version column input (KIR-575). Empty string and `*` mean match-all.
 */
export function validateVersionConstraintValue(
  raw: string
): string | undefined {
  const s = raw.trim();
  if (s === '') {
    return undefined;
  }
  if (s === '*') {
    return undefined;
  }
  try {
    return semver.validRange(s) !== null ? undefined : 'Invalid version range';
  } catch {
    return 'Invalid version range';
  }
}

function isMcpRowEffectivelyEmpty(fields: Record<string, string>): boolean {
  const t = (fields.transport ?? 'any').trim().toLowerCase();
  const transportDefault = t === '' || t === 'any';
  return (
    (fields.mcpNameOrId ?? '').trim() === '' &&
    (fields.version ?? '').trim() === '' &&
    (fields.publisher ?? '').trim() === '' &&
    transportDefault
  );
}

function isExtensionsRowEffectivelyEmpty(
  fields: Record<string, string>
): boolean {
  return (
    (fields.extensionId ?? '').trim() === '' &&
    (fields.version ?? '').trim() === '' &&
    (fields.publisher ?? '').trim() === ''
  );
}

/**
 * When the draft row is a “no meaningful data” placeholder (same rules as
 * {@link createAllowBlockValidateCell} / soft-delete semantics). MCP `transport`
 * defaults to `any` in the UI; that alone must not count as user content.
 */
export function isAllowBlockDraftEffectivelyEmpty(
  policyKey: AllowBlockPolicyKey,
  row: EditableRow
): boolean {
  if (policyKey === 'mcp-server-sources') {
    return isMcpRowEffectivelyEmpty(row.fields);
  }
  return isExtensionsRowEffectivelyEmpty(row.fields);
}

function rowMissingIdentifier(
  policyKey: AllowBlockPolicyKey,
  fields: Record<string, string>
): boolean {
  if (policyKey === 'mcp-server-sources') {
    if (isMcpRowEffectivelyEmpty(fields)) {
      return false;
    }
    return (
      (fields.mcpNameOrId ?? '').trim() === '' &&
      (fields.publisher ?? '').trim() === ''
    );
  }
  if (isExtensionsRowEffectivelyEmpty(fields)) {
    return false;
  }
  return (
    (fields.extensionId ?? '').trim() === '' &&
    (fields.publisher ?? '').trim() === ''
  );
}

function identifierColumnIds(policyKey: AllowBlockPolicyKey): string[] {
  return policyKey === 'mcp-server-sources'
    ? ['mcpNameOrId', 'publisher']
    : ['extensionId', 'publisher'];
}

function ruleObjectSignature(
  policyKey: AllowBlockPolicyKey,
  row: EditableRow
): string {
  const obj = policyRuleToExceptionRuleObject(policyKey, row);
  const keys = Object.keys(obj).sort();
  const sorted: Record<string, string> = {};
  for (const k of keys) {
    sorted[k] = obj[k] ?? '';
  }
  return JSON.stringify(sorted);
}

function isRowEffectivelyEmptyForDuplicate(
  policyKey: AllowBlockPolicyKey,
  row: EditableRow
): boolean {
  if (policyKey === 'mcp-server-sources') {
    return isMcpRowEffectivelyEmpty(row.fields);
  }
  return isExtensionsRowEffectivelyEmpty(row.fields);
}

function validateExtensionsManagementLiteralCell(
  columnId: string,
  rawValue: string,
  fields: Record<string, string>
): string | undefined {
  if (isExtensionsRowEffectivelyEmpty(fields)) {
    return undefined;
  }
  const trimmed = rawValue.trim();
  if (trimmed === '' || trimmed.includes('*')) {
    return undefined;
  }
  if (columnId === 'extensionId') {
    return isValidExtensionCompositeId(trimmed.toLowerCase())
      ? undefined
      : INVALID_EXTENSION_ID_MSG;
  }
  if (columnId === 'publisher') {
    return isValidPublisherName(trimmed) ? undefined : INVALID_PUBLISHER_MSG;
  }
  return undefined;
}

function isDuplicateRow(
  policyKey: AllowBlockPolicyKey,
  row: EditableRow,
  existingRows: EditableRow[]
): boolean {
  if (isRowEffectivelyEmptyForDuplicate(policyKey, row)) {
    return false;
  }
  const sig = ruleObjectSignature(policyKey, row);
  for (const other of existingRows) {
    if (other.pendingDelete) {
      continue;
    }
    if (other.id === row.id) {
      continue;
    }
    if (isRowEffectivelyEmptyForDuplicate(policyKey, other)) {
      continue;
    }
    if (ruleObjectSignature(policyKey, other) === sig) {
      return true;
    }
  }
  return false;
}

/**
 * Per-cell validator for MCP and Extensions allow/block tables:
 * semver on Version, “at least name or publisher” when the row is not empty,
 * and duplicate detection (same serialized ruleObject as another row — matches BE 409).
 */
export function createAllowBlockValidateCell(
  policyKey: AllowBlockPolicyKey,
  fallbackPeerRows: EditableRow[] = []
): ValidateCellFn {
  return (input): string | undefined => {
    const { row, column, value, peerRows } = input;
    const peerList = peerRows ?? fallbackPeerRows;
    const fields = row.fields;

    if (column.id === 'version') {
      const versionError = validateVersionConstraintValue(value);
      if (versionError !== undefined) {
        return versionError;
      }
      if (
        !rowMissingIdentifier(policyKey, fields) &&
        isDuplicateRow(policyKey, row, peerList)
      ) {
        return DUPLICATE_BADGE;
      }
      return undefined;
    }

    if (policyKey === 'extensions-management') {
      const literalError = validateExtensionsManagementLiteralCell(
        column.id,
        value,
        fields
      );
      if (literalError !== undefined) {
        return literalError;
      }
    }

    const ids = identifierColumnIds(policyKey);
    if (ids.includes(column.id) && rowMissingIdentifier(policyKey, fields)) {
      return MISSING_IDENTIFIER_BADGE;
    }

    if (rowMissingIdentifier(policyKey, fields)) {
      return undefined;
    }

    if (isDuplicateRow(policyKey, row, peerList)) {
      return DUPLICATE_BADGE;
    }

    return undefined;
  };
}
